import React, { PropsWithChildren } from 'react';
import { FormApi, SubmissionErrors } from 'final-form';
import {
  IQuestionnaire,
  IQuestionnaireItemGroupComponent,
  QuestionnaireItemComponent,
} from '../../unsw-fhir/Questionnaire';
import { IQuestionnaireResponse } from '../../unsw-fhir/QuestionnaireResponse';
import { IQuestionnaireContext, QuestionnaireContext } from '../Contexts';
import produce from 'immer';
import QuestionnaireMutator from '../Utilities/QuestionnaireMutator';

export interface AnyObject {
  [key: string]: any;
}

type onSaveHandler = (
  response: IQuestionnaireResponse,
  questionnaire: IQuestionnaire,
) =>
  | SubmissionErrors
  | Promise<SubmissionErrors | undefined>
  | undefined
  | void;

type onSubmitHandler = (
  onSave: onSaveHandler,
) => (
  values: AnyObject,
  form: FormApi<AnyObject>,
  callback?: (errors?: SubmissionErrors) => void,
) =>
  | SubmissionErrors
  | Promise<SubmissionErrors | undefined>
  | undefined
  | void;

export interface QuestionnaireProps {
  questionnaire: IQuestionnaire;
  response?: IQuestionnaireResponse;
  onComplete: (
    questionnaire: IQuestionnaire,
    questionnaireResponse?: IQuestionnaireResponse,
  ) => void | Promise<void>;
  onSave: onSaveHandler;
}

const setActive = (
  linkId: string,
  itemNode: IQuestionnaireItemGroupComponent | IQuestionnaire | null,
): [QuestionnaireItemComponent | IQuestionnaire | null, number] => {
  if (!itemNode) return [null, -1];
  const index = itemNode.item.findIndex(i => i.linkId === linkId);
  const nViewItem = itemNode.item[index];
  return [nViewItem, index];
};

export default class Questionnaire extends React.PureComponent<
  PropsWithChildren<QuestionnaireProps>,
  IQuestionnaireContext
> {
  private mutator: QuestionnaireMutator;
  private initialize = (props: QuestionnaireProps): IQuestionnaireContext => {
    const { itemView, itemIndex, itemNode } = this.mutator.getFirst();
    const progress = this.mutator.getProgress().toString();
    return {
      questionnaire: props.questionnaire,
      response: props.response,
      onSubmit: this.onSubmit(props.onSave),
      onNext: this.onNext,
      onPrevious: this.onPrevious,
      onComplete: this.onComplete,
      progress,
      isComplete: this.isComplete,
      isEnabled: this.isEnabled,
      first: this.mutator.isFirst(itemView, itemNode, itemIndex),
      last: this.mutator.isLast(itemView, itemNode, itemIndex),
      complete: progress === '100',
      sections: this.mutator.sections,
      setActive: this.setActive,
      itemView,
      itemIndex,
      itemNode,
      submitting: false,
    };
  };

  private isComplete = (linkId?: string) => this.mutator.isComplete(linkId);

  private isEnabled = (linkId: string) => this.mutator.isEnabled(linkId);

  private onComplete = async () =>
    await this.props.onComplete(this.state.questionnaire, this.state.response);

  private onSubmit: onSubmitHandler =
    onSave => async (values, form, errors) => {
      try {
        this.setState(state =>
          produce(state, draft => {
            draft.submitting = true;
            return draft;
          }),
        );
        const resp = this.mutator.setResponses(values);
        const cleaned = this.mutator.enforceConstraints(resp);
        return await onSave(cleaned, this.mutator.questionnaire);
      } catch (e) {
        return 'Could not set responese';
      } finally {
        this.setState(state =>
          produce(state, draft => {
            draft.submitting = false;
            return draft;
          }),
        );
      }
    };

  private onNext = () => {
    this.setState(state =>
      produce(state, draft => {
        if (!draft.itemNode)
          throw new Error('draft.itemNode should not be null or undefined');
        [draft.itemView, draft.itemIndex] = this.mutator.goNext(
          draft.itemNode,
          draft.itemIndex,
        );

        draft.first = this.mutator.isFirst(
          draft.itemView,
          draft.itemNode,
          draft.itemIndex,
        );
        draft.last = this.mutator.isLast(
          draft.itemView,
          draft.itemNode,
          draft.itemIndex,
        );
        return draft;
      }),
    );
  };

  private onPrevious = () => {
    this.setState(state =>
      produce(state, draft => {
        if (!draft.itemNode)
          throw new Error('draft.itemNode should not be null or undefined');
        [draft.itemView, draft.itemIndex] = this.mutator.goPrevious(
          draft.itemNode,
          draft.itemIndex,
        );

        draft.first = this.mutator.isFirst(
          draft.itemView,
          draft.itemNode,
          draft.itemIndex,
        );
        draft.last = this.mutator.isLast(
          draft.itemView,
          draft.itemNode,
          draft.itemIndex,
        );
        return draft;
      }),
    );
  };

  private setActive = (linkId: string) => {
    this.setState(state =>
      produce(state, draft => {
        if (!draft.itemNode)
          throw new Error('draft.itemNode should not be null or undefined');
        [draft.itemView, draft.itemIndex] = setActive(linkId, draft.itemNode);

        draft.last = this.mutator.isLast(
          draft.itemView,
          draft.itemNode,
          draft.itemIndex,
        );
        draft.first = this.mutator.isFirst(
          draft.itemView,
          draft.itemNode,
          draft.itemIndex,
        );
      }),
    );
  };

  constructor(props: QuestionnaireProps) {
    super(props);
    this.mutator = new QuestionnaireMutator(
      props.questionnaire,
      props.response,
    );
    this.state = this.initialize(props);
  }

  componentDidUpdate = () => {
    if (this.props.questionnaire !== this.state.questionnaire) {
      this.mutator = new QuestionnaireMutator(
        this.props.questionnaire,
        this.props.response,
      );
      this.setState(this.initialize(this.props));
      return;
    } else if (this.props.response !== this.state.response) {
      this.mutator.updateResponse(this.props.response);
      this.setState(state =>
        produce(state, draft => {
          if (!draft.itemNode)
            throw new Error('draft.itemNode should not be null or undefined');
          draft.response = this.props.response;
          draft.progress = this.mutator.getProgress().toString();
          draft.complete = draft.progress === '100';
          draft.sections = this.mutator.sections;
          draft.first = this.mutator.isFirst(
            draft.itemView,
            draft.itemNode,
            draft.itemIndex,
          );
          draft.last = this.mutator.isLast(
            draft.itemView,
            draft.itemNode,
            draft.itemIndex,
          );
          draft.sections = this.mutator.sections;
          return draft;
        }),
      );
    }
  };
  render = () => {
    return (
      <QuestionnaireContext.Provider value={this.state}>
        {this.props.children}
      </QuestionnaireContext.Provider>
    );
  };
}
